// @flow
import React from "react";
import { css } from "styled-components";
import { getImage } from "@nested/utils";
import { media } from "../../media";
import {
  regularHeading,
  mediumHeading,
} from "../../components/Typography/Headings";
import { mediumParagraph } from "../../components/Typography/Paragraphs";
import {
  Table,
  AgentTH,
  ComparisonTH,
  TR,
  TD,
  IconTD,
  FeeTD,
  FeesHeading,
} from "../../components/ComparisonTable";

const TopLeft = () => (
  <svg width="40px" height="31px" viewBox="0 0 40 31">
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <path
        d="M40,0 L40,0.001 L39.5506616,0 C30.0837013,0 22.233481,5.37473506 20.6696044,10.7494701 L20.3700449,10.7494701 L20.3700449,10.7494701 C14.4068295,10.7494701 9.57268818,15.5562883 9.57268818,21.4857991 C7.03425887,21.484637 4.59939705,22.4865101 2.80404322,24.2708948 C1.00868939,26.0552796 0,28.4759179 0,31 L0,0 L40,0 Z"
        fill="#FAF7F4"
      ></path>
    </g>
  </svg>
);

const TopRight = () => (
  <svg width="40px" height="31px" viewBox="0 0 40 31">
    <g
      id="top-right"
      stroke="none"
      strokeWidth="1"
      fill="none"
      fillRule="evenodd"
    >
      <path
        d="M40,0 L40,0.001 L39.5506616,0 C30.0837013,0 22.233481,5.37473506 20.6696044,10.7494701 L20.3700449,10.7494701 L20.3700449,10.7494701 C14.4068295,10.7494701 9.57268818,15.5562883 9.57268818,21.4857991 C7.03425887,21.484637 4.59939705,22.4865101 2.80404322,24.2708948 C1.00868939,26.0552796 0,28.4759179 0,31 L0,0 L40,0 Z"
        fill="#FAF7F4"
        transform="translate(20.000000, 15.500000) scale(-1, 1) translate(-20.000000, -15.500000) "
      ></path>
    </g>
  </svg>
);

const moneyDogWrapperStyle = css`
  display: none;
  @media (min-width: ${1100 / 16}em) {
    display: block;
    position: absolute;
    top: -15px;
    right: 40px;
    overflow: visible;
  }
`;

const imageStyle = css`
  height: 124px;
`;

const barkBarkStyle = css`
  display: block;
  position: absolute;
  top: 15px;
  right: -170px;
  height: 90px;
`;

const topLeftStyle = css`
  display: none;
  ${media.desktop`
    display: block;
    position: absolute;
    top: 0;
    left: 0;
  `};
`;

const topRightStyle = css`
  display: none;
  ${media.desktop`
    display: block;
    position: absolute;
    top: 0;
    right: 0;
  `};
`;

const backgroundStyle = css`
  background-color: white;
  padding: 40px 20px;
  ${media.desktop`
    background-color: ${({ theme }) => theme.palette.sand40};
    padding-top: 60px;
    padding-bottom: 0px;
  `}
`;

const tableWrapperStyle = css`
  ${media.desktop`
    position: relative;
    background-color: white;
    padding: 40px 0px;
  `}
`;

const headingWrapper = css`
  ${media.desktop`
  position: relative;
    width: calc(100% / 10 * 8);
    margin: auto;
    margin-bottom: 60px;
  `}
`;

const wrapperStyle = css`
  max-width: 1140px;
  width: 100%;
  margin: auto;
`;

const headingStyle = css`
  ${regularHeading}
  margin: 0px;
  white-space: pre-wrap;
  ${media.tablet`
    ${mediumHeading}
  `}
  ${media.desktop`
    white-space: normal;
  `}
`;

const contentStyle = css`
  width: 100%;
  max-width: 534px;
  margin: auto;
  ${media.desktop`
    max-width: none;
    width: calc(100% / 12 * 10);
  `}
`;

const mutedStyle = css`
  opacity: 0.5;
`;

type Props = {
  heading: string,
  subheading: string,
  repeat: {
    feature: string,
    nested: "Yes" | "No",
    highStreet: "Yes" | "No",
    online: "Yes" | "No",
  }[],
};

export const ComparisonTable = ({ heading, subheading, repeat }: Props) => (
  <div css={backgroundStyle}>
    <div css={wrapperStyle}>
      <div css={contentStyle}>
        <div css={headingWrapper}>
          <div css={moneyDogWrapperStyle}>
            <img
              css={imageStyle}
              alt=""
              src={getImage("illustrations/money-dog-floor.png")}
            />
            <img
              css={barkBarkStyle}
              alt=""
              src={getImage("illustrations/bark-bark.png")}
            />
          </div>
          <h3 css={headingStyle}>{heading}</h3>
          <p css={mediumParagraph}>{subheading}</p>
        </div>
        <div css={tableWrapperStyle}>
          <div css={topLeftStyle}>
            <TopLeft />
          </div>
          <div css={topRightStyle}>
            <TopRight />
          </div>
          <Table>
            <thead>
              <tr>
                <AgentTH>Agent</AgentTH>
                <ComparisonTH>Nested</ComparisonTH>
                <ComparisonTH>High Street</ComparisonTH>
                <ComparisonTH>Online</ComparisonTH>
              </tr>
            </thead>
            <tbody>
              {repeat.map(({ feature, nested, highStreet, online }) => (
                <TR>
                  <TD>{feature}</TD>
                  <IconTD value={nested} />
                  <IconTD value={highStreet} />
                  <IconTD value={online} />
                </TR>
              ))}
              <TR>
                <FeesHeading>
                  Fees <span css={mutedStyle}>inc. VAT</span>
                </FeesHeading>
                <FeeTD>1.5%</FeeTD>
                <FeeTD>1.75%</FeeTD>
                <FeeTD>£1500</FeeTD>
              </TR>
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  </div>
);
